import { useState, useRef, useCallback, useEffect } from 'react'
import { Mic, Square, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { supabase } from '@/lib/supabase'
import { useToast } from '@/hooks/use-toast'
import { getApiBaseUrl } from '@/lib/api-url'

const API_BASE_URL = getApiBaseUrl()

interface AudioRecorderProps {
  onTranscribed: (text: string) => void
  disabled?: boolean
}

export function AudioRecorder({ onTranscribed, disabled }: AudioRecorderProps) {
  const [state, setState] = useState<'idle' | 'recording' | 'transcribing' | 'permission_denied'>('idle')
  const [duration, setDuration] = useState(0)
  const { toast } = useToast()

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAllTracks()
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [])

  const stopAllTracks = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }
  }, [])

  const transcribeAudio = async (blob: Blob) => {
    setState('transcribing')

    try {
      // Get auth token
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData.session?.access_token

      if (!token) {
        throw new Error('Não autenticado')
      }

      // Create form data with the audio blob
      const formData = new FormData()
      formData.append('audio', blob, 'recording.webm')

      // Call the NestJS API
      const response = await fetch(`${API_BASE_URL}/api/ai/transcribe`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || 'Transcription failed')
      }

      const data = await response.json()

      if (!data.success) {
        throw new Error(data.error || 'Transcription failed')
      }

      // Insert the transcribed text into the editor
      onTranscribed(data.text)

      toast({
        title: 'Áudio transcrito',
        description: 'O texto foi inserido no registro.',
      })
    } catch (err) {
      console.error('Transcription error:', err)
      toast({
        title: 'Erro na transcrição',
        description: err instanceof Error ? err.message : 'Não foi possível transcrever o áudio. Tente novamente.',
        variant: 'destructive',
      })
    } finally {
      setState('idle')
      setDuration(0)
    }
  }

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
        },
      })
      streamRef.current = stream

      // Determine supported MIME type
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm'

      const recorder = new MediaRecorder(stream, { mimeType })
      mediaRecorderRef.current = recorder
      chunksRef.current = []

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data)
        }
      }

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType })
        stopAllTracks()
        // Automatically transcribe when recording stops
        transcribeAudio(blob)
      }

      recorder.start(1000) // 1s chunks
      setState('recording')
      setDuration(0)

      timerRef.current = setInterval(() => {
        setDuration((d) => d + 1)
      }, 1000)
    } catch (err) {
      console.error('Microphone permission denied:', err)
      setState('permission_denied')
    }
  }

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop()
    }
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
  }

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  if (state === 'permission_denied') {
    return (
      <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 rounded-lg p-3">
        <Mic className="h-4 w-4 flex-shrink-0" />
        <span>Permissão de microfone negada. Verifique as configurações do navegador.</span>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setState('idle')}
          className="ml-auto flex-shrink-0"
        >
          Tentar novamente
        </Button>
      </div>
    )
  }

  if (state === 'transcribing') {
    return (
      <div className="flex items-center gap-2 bg-teal-50 rounded-lg px-3 py-2">
        <Loader2 className="h-4 w-4 animate-spin text-teal-600" />
        <span className="text-sm text-teal-700">Transcrevendo áudio...</span>
      </div>
    )
  }

  if (state === 'recording') {
    return (
      <div className="flex items-center gap-2 bg-red-50 rounded-lg px-3 py-2">
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" />
        </span>
        <span className="text-sm text-red-600 tabular-nums">{formatTime(duration)}</span>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={stopRecording}
          className="ml-2"
        >
          <Square className="h-3.5 w-3.5 mr-1" />
          Parar e transcrever
        </Button>
      </div>
    )
  }

  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      onClick={startRecording}
      disabled={disabled}
    >
      <Mic className="h-4 w-4 mr-1" />
      Ditar
    </Button>
  )
}
